import { Plus } from "lucide-react";
import { type ChangeEvent, useContext } from "react";
import type { NavigateFunction } from "react-router-dom";

import { Checkbox } from "@/app/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/app/components/ui/tooltip";
import ResumeContext from "@/app/context/ResumeContext";
import { cn } from "@/app/lib/utils";
import type { ResumeContextValue, SelectedExtras } from "@/app/types/resume";

export interface ExtrasSection {
	id: string;
	label: string;
	name: string;
	link: string;
}

interface ExtrasCheckboxProps {
	section: ExtrasSection;
	extrasChoicesHandler: (event: ChangeEvent<HTMLInputElement>) => void;
	navigate: NavigateFunction;
}

function ExtrasCheckbox({ section, extrasChoicesHandler, navigate }: ExtrasCheckboxProps) {
	const { resumeData } = useContext(ResumeContext) as ResumeContextValue;
	const selectedExtras = resumeData.extras.selectedExtras;

	const isChecked = Boolean(selectedExtras[section.id as keyof SelectedExtras]);

	// The handler upstream reads a change event off a native input. Radix reports
	// the new state directly, so hand it the shape it expects.
	const setChecked = (checked: boolean) => {
		extrasChoicesHandler({
			target: { name: section.name, id: section.name, checked, type: "checkbox" },
		} as ChangeEvent<HTMLInputElement>);
	};

	return (
		<div
			className={cn(
				"mb-3 flex items-center justify-between rounded-md border border-gray-300 border-dashed px-3 py-2 transition-colors hover:border-primary-500",
				isChecked && "bg-primary-50",
			)}
		>
			{/* The row used to be a bare `<div onClick>` wrapping a `className="hidden"`
			    input. `display: none` takes an element out of the accessibility tree, so
			    the state was never announced and Tab skipped the row entirely — the whole
			    extras selection could only be operated with a mouse. A real label bound to
			    a Radix checkbox restores the click target, the focus ring, Space to toggle,
			    and the announced checked state. */}
			<label
				htmlFor={section.name}
				className="m-0 flex flex-1 cursor-pointer items-center gap-3 font-semibold text-gray-900"
			>
				<Checkbox id={section.name} name={section.name} checked={isChecked} onCheckedChange={setChecked} />
				{section.label}
			</label>
			{isChecked ? (
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							type="button"
							onClick={() => navigate(section.link)}
							className="cursor-pointer font-semibold text-primary-700"
						>
							Update
						</button>
					</TooltipTrigger>
					<TooltipContent side="right" variant="plain">
						Click to Add/Edit
					</TooltipContent>
				</Tooltip>
			) : (
				<Plus className="size-4 text-gray-600" aria-hidden="true" />
			)}
		</div>
	);
}

export default ExtrasCheckbox;
